// Pixar Talking Ad's script as live's form reads it (spec G14-pixar-talking-ad, G14 PR 18a;
// live's `pixar-talking-ad-form/core/director/script.ts` and `library-view/constants.ts`, read as
// a behaviour spec): the 8 beats, the blank script "Write the 8 lines yourself" fills, the meta
// line, and what keeps the script from approval. The server checks the same on Approve
// (`pixar_talking_ad_draft.lines_issue`).
import type { PixarScript } from "../../../api/generated/model";
import { countWords } from "./podcast-script";

/** Live's words (`core/constants.ts`, `library-view/constants.ts`). */
export const PICK_PRODUCT = "Pick a product and its packshot";
export const GENERATE_SCRIPT = "Generate or write the script";
export const ACTOR_PENDING = "Wait for the AI Actor to finish";
export const STILLS_PENDING = "Wait for all five images";
export const SCRIPT_SHAPE = "The script needs its 8 lines in order — generate it again";
export const EMPTY_LINE = "Every line needs its text";
export const UNREADABLE_PACK =
  "The pack was hard to read — type the product name or its text and generate again.";

/** Live's 8 beats in speaking order and each one's most words (`BEATS`; capture 156). */
export const BEATS: readonly { beat: string; maxWords: number }[] = [
  { beat: "hook", maxWords: 16 },
  { beat: "what_people_do", maxWords: 14 },
  { beat: "why_it_fails", maxWords: 14 },
  { beat: "product_line", maxWords: 8 },
  { beat: "proof", maxWords: 22 },
  { beat: "proof_2", maxWords: 16 },
  { beat: "closer", maxWords: 16 },
  { beat: "cta", maxWords: 8 },
];
/** Live's `WORD_TARGET`, its pace in words a second and the clips' edges in seconds. */
const WORD_TARGET = { min: 75, max: 100 };
const WORDS_PER_SECOND = 3.2;
const CLIP_EDGES_S = 1;

/** The most words line `index` has (live's `getMaxWords`). */
export function maxWordsAt(index: number): number {
  return BEATS[index]?.maxWords ?? 0;
}

function scriptWords(lines: PixarScript["lines"]): number {
  return lines.reduce((total, line) => total + countWords(line.text), 0);
}

/** Live's meta line (`toScriptMeta`): "8 lines · 0 words (75–100) · ≈ 1 s", the seconds at
 * live's pace plus the clips' edges, to a tenth. */
export function scriptMeta(lines: PixarScript["lines"]): string {
  const seconds = Math.round((scriptWords(lines) / WORDS_PER_SECOND + CLIP_EDGES_S) * 10) / 10;
  const { min, max } = WORD_TARGET;
  return `${lines.length} lines · ${scriptWords(lines)} words (${min}–${max}) · ≈ ${seconds} s`;
}

/** "Write the 8 lines yourself" (live's `toBlankScript`): a blank line a beat and nothing else;
 * Approve script fills the rest from the form (the server's `approved_script`). */
export function blankScript(): PixarScript {
  return {
    problem: "",
    old_habit: "",
    product_short_name: "",
    mechanism: "",
    claims: [],
    lines: BEATS.map(({ beat }, index) => ({ id: index + 1, beat, text: "", claim_ids: [] })),
    pronunciations: [],
    word_count: 0,
    estimated_duration_s: 0,
    end_card: { headline: "", cta_button: "", offer_line: "" },
  };
}

/** What keeps the lines from approval (live's `toLinesIssue`, and ours: each line numbered in its
 * beat, G14 Pixar Talking Ad-8), or null. */
export function scriptIssue(script: PixarScript | null): string | null {
  if (script === null) return GENERATE_SCRIPT;
  const inOrder =
    script.lines.length === BEATS.length &&
    script.lines.every((line, at) => line.id === at + 1 && line.beat === BEATS[at]?.beat);
  if (!inOrder) return SCRIPT_SHAPE;
  return script.lines.some((line) => line.text.trim() === "") ? EMPTY_LINE : null;
}

/** Live's warning after the director's redo (`toScriptProblemsMessage`). */
export function scriptProblems(problems: readonly string[]): string {
  return `The script check failed twice: ${problems.join("; ")}. Fix the lines before approving.`;
}
